import React, { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const isMobileDevice = () => {
  // Check if device is mobile
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

  // Mobile regex patterns
  const mobilePatterns = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
    /IEMobile/i,
    /Mobile/i,
    /Opera Mini/i,
  ];

  const isMobile = mobilePatterns.some((pattern) => pattern.test(userAgent));

  // Also check screen width for tablets/small devices
  const isSmallScreen = window.innerWidth <= 1024;

  return isMobile || isSmallScreen;
};

const PWAPrompt: React.FC = () => {
  const [installPrompt, setInstallPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if device is mobile on mount
    setIsMobile(isMobileDevice());

    // Check on resize as well
    const handleResize = () => {
      setIsMobile(isMobileDevice());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Only listen for install prompt on mobile devices
    if (!isMobile) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, [isMobile]);

  const handleInstallClick = async () => {
    if (!installPrompt) return;

    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;

    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
  };

  // Don't show on desktop
  if (!showPrompt || !isMobile) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 bg-white rounded-lg shadow-xl p-4 mx-4 border border-gray-200 z-50 md:max-w-md md:left-auto md:right-4 md:mx-0">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            ติดตั้งแอปพลิเคชัน
          </h3>
          <p className="text-gray-600 mt-1">
            ติดตั้งแอปพลิเคชันเพื่อการใช้งานที่สะดวกยิ่งขึ้น
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowPrompt(false)}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            ไม่ต้องการ
          </button>
          <button
            onClick={handleInstallClick}
            className="px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700"
          >
            ติดตั้ง
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAPrompt;
